import { AnimatePresence, motion } from "framer-motion";
import type { CountryFeature } from "../types";
import {
  getName,
  getCode,
  getContinent,
  getPopulation,
  formatNumber,
} from "../utils/country";
import { catalogFor, type Show } from "../utils/catalog";

interface Props {
  feature: CountryFeature | null;
  onClose: () => void;
  centroid?: { lat: number; lng: number } | null;
}

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06, delayChildren: 0.12 } },
};

const rise = {
  hidden: { opacity: 0, y: 22 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.2, 0.8, 0.2, 1] as const },
  },
};

function Poster({ show, rank }: { show: Show; rank?: number }) {
  return (
    <motion.article
      className={`nf-card ${rank ? "ranked" : ""}`}
      whileHover={{ scale: 1.05, y: -3 }}
      transition={{ type: "spring", stiffness: 340, damping: 28 }}
    >
      {rank ? <span className="nf-rank">{rank}</span> : null}
      <div className="nf-poster" style={{ background: show.gradient }}>
        {show.badge ? <span className="nf-badge">{show.badge}</span> : null}
        <div className="nf-poster-overlay">
          <div className="nf-poster-title">{show.title}</div>
          <div className="nf-poster-meta">
            <span>{show.year}</span>
            <span className="sep">·</span>
            <span>{show.genre}</span>
          </div>
        </div>
      </div>
      <div className="nf-card-info">
        <span className="nf-match">{show.match}% match</span>
        <span className="nf-duration">
          {show.episodes} eps · {show.duration}
        </span>
      </div>
    </motion.article>
  );
}

function RowCard({
  title,
  shows,
  ranked,
}: {
  title: string;
  shows: Show[];
  ranked?: boolean;
}) {
  return (
    <motion.section className="g-card g-card-row" variants={rise}>
      <h2 className="g-row-title">{title}</h2>
      <div className="g-row-scroll">
        {shows.map((s, i) => (
          <Poster key={s.id + i} show={s} rank={ranked ? i + 1 : undefined} />
        ))}
      </div>
    </motion.section>
  );
}

export default function CountryPanel({ feature, onClose }: Props) {
  if (!feature) return <AnimatePresence />;
  const name = getName(feature.properties);
  const code = getCode(feature.properties);
  const continent = getContinent(feature.properties);
  const pop = getPopulation(feature.properties);
  const catalog = catalogFor(code);

  return (
    <AnimatePresence>
      <motion.aside
        key={code}
        className="cards-panel"
        initial="hidden"
        animate="show"
        exit={{ opacity: 0, x: 24, transition: { duration: 0.32 } }}
        variants={stagger}
      >
        <motion.button
          className="g-close"
          onClick={onClose}
          variants={rise}
          aria-label="Close"
        >
          ✕
        </motion.button>

        {/* HERO CARD */}
        <motion.article className="g-card g-card-hero" variants={rise}>
          <div
            className="g-hero-image"
            style={{ background: catalog.featured.gradient }}
          >
            <div className="g-hero-scrim" />
            <span className="g-hero-featured-badge">
              {catalog.featured.badge}
            </span>
            <div className="g-hero-featured-title">
              {catalog.featured.title}
            </div>
          </div>
          <div className="g-hero-body">
            <div className="g-hero-eyebrow">
              <span className="g-eyebrow-dot" />
              TOP CONTENT IN
            </div>
            <h1 className="g-country-name">{name}</h1>
            <div className="g-country-meta">
              <span>{pop ? `${formatNumber(pop)} viewers` : "—"}</span>
              <span className="sep">·</span>
              <span>{continent}</span>
              <span className="sep">·</span>
              <span>{code}</span>
            </div>
            <div className="g-hero-actions">
              <button className="g-play">
                <svg width="12" height="12" viewBox="0 0 14 14" aria-hidden>
                  <path d="M2 1 L12 7 L2 13 Z" fill="currentColor" />
                </svg>
                Play
              </button>
              <button className="g-more">+ My List</button>
            </div>
          </div>
        </motion.article>

        <RowCard title={`Top 10 in ${name}`} shows={catalog.top10} ranked />
        <RowCard title="Trending Now" shows={catalog.trending} />
        <RowCard title="Popular Series" shows={catalog.popular} />
        <RowCard title="Because You Watched Orbit" shows={catalog.becauseYouWatched} />

        <motion.div className="g-footer" variants={rise}>
          <span className="dot live" />
          {/* ⚠ THIS LINE RENDERED "live from samba tv" — a real company's name,
              attached to a fabricated catalog, on screen. It is the single worst
              instance of that in this demo, because a viewer reads it as
              provenance for the data above it. `synthetic` is the accurate word
              and it says so at the exact place the claim would otherwise be made. */}
          <span>
            synthetic catalog · {code} · {name}
          </span>
        </motion.div>
      </motion.aside>
    </AnimatePresence>
  );
}
